import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import './ExplorerBoard.css';
import JourneyList from './JourneyList';
import JourneySearchModal from './JourneySearchModal';
import './JourneySearchModal.css'; // Reuse styles from JourneySearchModal
import CreateJourneyModal from './CreateJourneyModal';

const ExplorerBoard = ({
  journeys = [],
  totalJourneys = 0,
  filters = {},
  onFilterChange,
  onFilterSelect,
  selectedFilter,
  clearFilters,
  filtersVisible,
  onToggleFilters,
  onCreateJourney,
  onAddJourney,
  onJourneySelect,
  onJourneyUpdate,
  selectedJourneyId,
  isLoading = false,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  journeysPerPage = 10,
  onPageSizeChange,
  onCenterMap,
  userPlan = 'Basic',
  notes = [],
  userAddress = null,
  calculatingJourneyIds = new Set()
}) => {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [reviewJourneyData, setReviewJourneyData] = useState(null); // Journey data for review mode
  const [createModalSource, setCreateModalSource] = useState(null); // 'polygonEdit' (map/detail) | 'websocketReview'
  const [selectedJourneyDetails, setSelectedJourneyDetails] = useState(null);
  const [isLoadingJourneyDetails, setIsLoadingJourneyDetails] = useState(false);
  const [locationSortBy, setLocationSortBy] = useState('alphabetical'); // 'alphabetical', 'distanceFromHome', 'distanceFromNote'
  const [currentUserId, setCurrentUserId] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  // Get current user ID for ownership checks
  useEffect(() => {
    const getCurrentUserId = async () => {
      try {
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setCurrentUserId(payload.sub || null);
        }
      } catch (e) {
        console.warn('Could not get current user ID:', e);
      }
    };
    getCurrentUserId();
  }, []);

  // Listen for journey review events from MainApp
  useEffect(() => {
    const handleOpenJourneyReview = async (event) => {
      const { journeyID, journeyData, geospatialBoundaries } = event.detail;
      
      // Fetch full journey details if needed
      let fullJourneyData = journeyData;
      if (!fullJourneyData || !fullJourneyData.geospatialBoundaries) {
        try {
          const token = (await fetchAuthSession()).tokens?.idToken?.toString();
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          };
          // Use dataType from journeyData if available, otherwise default to geojson
          const dataType = journeyData?.dataType || 'geojson';
          const response = await axios.get(`${apiUrl}/journeys/${journeyID}?dataType=${dataType}`, { headers });
          fullJourneyData = response.data;
        } catch (error) {
          console.error('Error fetching journey for review:', error);
          alert('Failed to load journey for review');
          return;
        }
      }
      
      // Set review data and open modal (websocket review - allow Cancel & Delete)
      setReviewJourneyData({
        ...fullJourneyData,
        geospatialBoundaries: geospatialBoundaries || fullJourneyData.geospatialBoundaries
      });
      setCreateModalSource('websocketReview');
      setIsCreateModalOpen(true);
    };
    
    window.addEventListener('openJourneyReview', handleOpenJourneyReview);
    return () => {
      window.removeEventListener('openJourneyReview', handleOpenJourneyReview);
    };
  }, [apiUrl]);

  // Listen for journey polygon edit events (from map popup Edit button)
  useEffect(() => {
    const handleOpenJourneyPolygonEdit = async (event) => {
      const { journeyID, journey } = event.detail || {};
      if (!journeyID) return;

      let journeyWithGeo = journey;
      if (!journeyWithGeo || !journeyWithGeo.geospatialBoundaries) {
        try {
          const token = (await fetchAuthSession()).tokens?.idToken?.toString();
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          };
          const dataType = journey?.dataType || 'geojson';
          const response = await axios.get(`${apiUrl}/journeys/${journeyID}?dataType=${dataType}`, { headers });
          journeyWithGeo = response.data;
        } catch (error) {
          console.error('Error fetching journey for polygon edit:', error);
          alert('Failed to load journey for editing');
          return;
        }
      }

      setReviewJourneyData({
        ...journeyWithGeo,
        journeyID: journeyID,
        geospatialBoundaries: journeyWithGeo.geospatialBoundaries
      });
      setCreateModalSource('polygonEdit'); // Cancel only, no delete
      setIsCreateModalOpen(true);
    };

    window.addEventListener('openJourneyPolygonEdit', handleOpenJourneyPolygonEdit);
    return () => {
      window.removeEventListener('openJourneyPolygonEdit', handleOpenJourneyPolygonEdit);
    };
  }, [apiUrl]);

  // Fetch journey details when a journey is selected
  useEffect(() => {
    const fetchJourneyDetails = async () => {
      if (!selectedJourneyId) {
        setSelectedJourneyDetails(null);
        return;
      }

      // Find the journey in the journeys list
      const journey = journeys.find(j => j.journeyID === selectedJourneyId);
      if (!journey) {
        setSelectedJourneyDetails(null);
        return;
      }

      // If journey already has full details (geospatialBoundaries), use it
      if (journey.geospatialBoundaries) {
        setSelectedJourneyDetails(journey);
        return;
      }

      // Otherwise fetch full details
      setIsLoadingJourneyDetails(true);
      try {
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
        
        // Use dataType from journey if available, otherwise default to geojson
        const dataType = journey?.dataType || 'geojson';
        const response = await axios.get(`${apiUrl}/journeys/${selectedJourneyId}?dataType=${dataType}`, { headers });
        // Merge fetched details with user-specific metadata (preserve ownerID/public for Edit button visibility)
        setSelectedJourneyDetails({
          ...response.data,
          journeyID: selectedJourneyId,
          ownerID: response.data.ownerID ?? journey.ownerID,
          public: response.data.public ?? journey.public,
          journeyColor: journey.journeyColor,
          displayItemsMode: journey.displayItemsMode,
          overrides: journey.overrides || [],
          isActive: journey.isActive,
          manualPercentComplete: journey.manualPercentComplete,
          journeyProgress: journey.journeyProgress || [], // Include journey progress
          visitedContinents: journey.visitedContinents || [], // Include visited continents for continentStyling
          displayJourney: journey.displayJourney,
          isComplete: journey.isComplete,
          isFavorite: journey.isFavorite
        });
      } catch (error) {
        console.error('Error fetching journey details:', error);
        // Fallback to using the journey from the list
        setSelectedJourneyDetails(journey);
      } finally {
        setIsLoadingJourneyDetails(false);
      }
    };

    fetchJourneyDetails();
  }, [selectedJourneyId, journeys, apiUrl]);

  const handleBackToExplorerBoard = () => {
    if (onJourneySelect) {
      onJourneySelect(null);
    }
    setSelectedJourneyDetails(null);
  };

  // Parse GeoJSON features and extract properties
  const getGeoJSONFeatures = (journey = selectedJourneyDetails) => {
    if (!journey?.geospatialBoundaries) return [];
    
    const geoData = journey.geospatialBoundaries;
    
    // Handle GeoJSON FeatureCollection format
    if (geoData && geoData.type === 'FeatureCollection' && Array.isArray(geoData.features)) {
      return geoData.features;
    }
    
    // Handle object-based format (boundingbox) - convert to array format
    if (geoData && typeof geoData === 'object' && !Array.isArray(geoData) && !geoData.type) {
      return Object.keys(geoData).map(locationName => {
        const locationData = geoData[locationName];
        return {
          name: locationName,
          properties: locationData.properties || {
            name: locationName,
            ...locationData
          },
          bounds: locationData.bounds || null
        };
      });
    }
    
    return [];
  };

  // Calculate the centerpoint of a location's bounding box or polygon
  const getLocationCenter = (feature) => {
    // Handle boundingbox format
    if (feature.bounds) {
      const { minLon, maxLon, minLat, maxLat } = feature.bounds;
      return {
        lat: (minLat + maxLat) / 2,
        lng: (minLon + maxLon) / 2
      };
    }
    
    // Handle GeoJSON Polygon format
    if (feature.geometry && feature.geometry.type === 'Polygon' && feature.geometry.coordinates) {
      const coordinates = feature.geometry.coordinates[0]; // Outer ring
      let sumLat = 0;
      let sumLng = 0;
      let count = 0;
      
      coordinates.forEach(coord => {
        sumLng += coord[0]; // longitude
        sumLat += coord[1]; // latitude
        count++;
      });
      
      return {
        lat: sumLat / count,
        lng: sumLng / count
      };
    }
    
    return null;
  };

  // Utility function to calculate distance between two coordinates using Haversine formula
  // Returns distance in miles
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Format distance for display
  const formatDistance = (distanceInMiles) => {
    if (distanceInMiles < 1) {
      return `${Math.round(distanceInMiles * 5280)} feet`;
    } else if (distanceInMiles < 10) {
      return `${distanceInMiles.toFixed(1)} miles`;
    } else {
      return `${Math.round(distanceInMiles)} miles`;
    }
  };

  // Helper function to validate and parse lat_long values
  const parseAndValidateLatLong = (latLongValue) => {
    // Check if value exists and is not null/undefined/empty
    if (!latLongValue || typeof latLongValue !== 'string') {
      return null;
    }

    // Check for invalid values like "TBD", "N/A", etc.
    const upperValue = latLongValue.trim().toUpperCase();
    if (upperValue === 'TBD' || upperValue === 'N/A' || upperValue === 'NULL' || upperValue === '') {
      return null;
    }

    // Try to parse the coordinates
    try {
      const parts = latLongValue.split(',').map(coord => coord.trim());
      if (parts.length !== 2) {
        return null;
      }

      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);

      // Validate that both are numbers and finite
      if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
        return null;
      }

      // Validate ranges: lat must be between -90 and 90, lng between -180 and 180
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return null;
      }

      return { lat, lng };
    } catch (error) {
      return null;
    }
  };

  // Helper function to find closest note for a location
  const getClosestNote = (center) => {
    if (!center || !center.lat || !center.lng) return null;

    const notesWithCoords = notes.filter(note => {
      const latLng = parseAndValidateLatLong(note.aiPromptResults?.lat_long?.value);
      return latLng !== null;
    });

    if (notesWithCoords.length === 0) return null;

    let minDistance = Infinity;
    let closestNote = null;

    notesWithCoords.forEach(note => {
      const latLng = parseAndValidateLatLong(note.aiPromptResults?.lat_long?.value);
      if (latLng) {
        const distance = calculateDistance(center.lat, center.lng, latLng.lat, latLng.lng);

        if (distance < minDistance) {
          minDistance = distance;
          closestNote = { ...note, distance: minDistance };
        }
      }
    });

    return closestNote;
  };

  // Calculate distances for a location and return formatted tooltip text
  const getLocationDistanceTooltip = (center, isClickable) => {
    if (!center || !center.lat || !center.lng) {
      return isClickable ? 'Click to center map on this location' : '';
    }

    let tooltipText = isClickable ? 'Click to center map on this location' : '';
    let distanceFromHome = null;
    const closestNote = getClosestNote(center);

    // Calculate distance from user's home address if available
    if (userAddress && userAddress.lat && userAddress.lng) {
      distanceFromHome = calculateDistance(
        userAddress.lat,
        userAddress.lng,
        center.lat,
        center.lng
      );
    }

    // Build tooltip text with distance information
    if (distanceFromHome !== null || closestNote) {
      if (tooltipText) tooltipText += '\n\n';
      tooltipText += 'Distance Information:\n';
      if (distanceFromHome !== null) {
        tooltipText += `📍 From your address: ${formatDistance(distanceFromHome)}\n`;
      } else {
        tooltipText += '📍 From your address: Not available (set address in Account Settings)\n';
      }
      if (closestNote) {
        const closestNoteName = closestNote.manualPromptResults?.emailSubject || closestNote.emailSubject || 'Note';
        tooltipText += `📝 From closest note (${closestNoteName}): ${formatDistance(closestNote.distance)}`;
      } else {
        tooltipText += '📝 From closest note: No notes with location data';
      }
    }

    return tooltipText || undefined;
  };

  // Handle location click to center map
  const handleLocationClick = (feature) => {
    if (!onCenterMap) return;
    
    const center = getLocationCenter(feature);
    if (center) {
      onCenterMap(center.lat, center.lng);
    }
  };

  return (
    <div className="explorer-board">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">{selectedJourneyDetails ? selectedJourneyDetails.title : 'My Explorer Board'}</h1>
          </div>
          
          <div className="header-actions">
            <button
              className={`filter-toggle-btn modern-icon-button ${filtersVisible ? 'active' : ''}`}
              onClick={onToggleFilters}
              title="Filter Journeys"
            >
              <i className="fas fa-filter"></i>
              <span>Filters</span>
            </button>

            <div className="journey-buttons-group">
              <button
                className="create-journey-btn modern-icon-button"
                onClick={() => setIsSearchModalOpen(true)}
                title="Add Journey"
              >
                <i className="fas fa-plus-circle"></i>
                <span>Add Journey</span>
              </button>

              <button
                className="create-journey-btn modern-icon-button primary"
                onClick={() => setIsCreateModalOpen(true)}
                title="Create New Journey"
              >
                <i className="fas fa-plus"></i>
                <span>Create Journey</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      {filtersVisible && (
        <div className="filters-section">
          <div className="filters-header">
            <h3>Filter Journeys</h3>
            <button className="clear-filters-btn modern-icon-button" onClick={clearFilters}>
              <i className="fas fa-times"></i>
              <span>Clear All</span>
            </button>
          </div>
          
          <div className="filters-grid">
            <div className="filter-group">
              <label className="filter-label">Filter Type</label>
              <select 
                className="filter-select" 
                onChange={(e) => onFilterSelect(e.target.value)}
                value={selectedFilter || ''}
              >
                <option value="">Select Filter</option>
                <option value="journeyName">Journey Name</option>
                <option value="progressRange">Progress Range</option>
                <option value="isComplete">Completion Status</option>
                <option value="isActive">Active Status</option>
                <option value="isFavorite">Favorite Status</option>
              </select>
            </div>

            {selectedFilter && selectedFilter !== 'isComplete' && selectedFilter !== 'isActive' && selectedFilter !== 'isFavorite' && (
              <div className="filter-group">
                <label className="filter-label">Filter Value</label>
                {selectedFilter === 'progressRange' ? (
                  <select
                    name="progressRange"
                    className="filter-select"
                    value={filters.progressRange || ''}
                    onChange={onFilterChange}
                  >
                    <option value="">All Progress</option>
                    <option value="0-25">0% - 25%</option>
                    <option value="25-50">25% - 50%</option>
                    <option value="50-75">50% - 75%</option>
                    <option value="75-100">75% - 100%</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    name={selectedFilter}
                    className="filter-input"
                    placeholder="Filter by journey name"
                    value={filters[selectedFilter] || ''}
                    onChange={onFilterChange}
                  />
                )}
              </div>
            )}

            {/* Boolean filters - show checkboxes when selected */}
            {selectedFilter === 'isComplete' && (
              <div className="filter-group">
                <label className="filter-label">Filter Value</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="isComplete"
                      checked={filters.isComplete === 'true'}
                      onChange={onFilterChange}
                    />
                    <span>Completed</span>
                  </label>
                </div>
              </div>
            )}

            {selectedFilter === 'isActive' && (
              <div className="filter-group">
                <label className="filter-label">Filter Value</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '8px' }}>
                  <label 
                    htmlFor="filter-isActive"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <input
                      id="filter-isActive"
                      type="checkbox"
                      name="isActive"
                      checked={filters.isActive === 'true'}
                      onChange={onFilterChange}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
            )}

            {selectedFilter === 'isFavorite' && (
              <div className="filter-group">
                <label className="filter-label">Filter Value</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '8px' }}>
                  <label 
                    htmlFor="filter-isFavorite"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <input
                      id="filter-isFavorite"
                      type="checkbox"
                      name="isFavorite"
                      checked={filters.isFavorite === 'true'}
                      onChange={onFilterChange}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <span>Favorite</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="dashboard-content">
        {selectedJourneyDetails ? (
          /* Journey Details View */
          <div className="journey-details-view" style={{ width: '100%', height: '100%' }}>
            {isLoadingJourneyDetails ? (
              <div className="details-loading">
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: 'var(--text-tertiary)', marginBottom: 'var(--space-4)' }}></i>
                <p>Loading journey details...</p>
              </div>
            ) : selectedJourneyDetails ? (
              <>
                <div className="details-header">
                  <h2>{selectedJourneyDetails.title}</h2>
                  <div className="details-header-actions">
                    {(() => {
                      const isUserOwned = selectedJourneyDetails.ownerID && selectedJourneyDetails.ownerID !== 'System' && selectedJourneyDetails.ownerID === currentUserId;
                      const isPrivate = selectedJourneyDetails.public === false || selectedJourneyDetails.public === 'false';
                      const hasEditableBoundaries = selectedJourneyDetails.geospatialBoundaries && !selectedJourneyDetails.dataSetID;
                      const showEdit = isUserOwned && isPrivate && hasEditableBoundaries;
                      return showEdit ? (
                        <button
                          className="edit-button"
                          onClick={() => {
                            setReviewJourneyData({ ...selectedJourneyDetails, journeyID: selectedJourneyDetails.journeyID });
                            setCreateModalSource('polygonEdit'); // Cancel only, no delete
                            setIsCreateModalOpen(true);
                          }}
                        >
                          <i className="fas fa-edit"></i>
                          <span>Edit</span>
                        </button>
                      ) : null;
                    })()}
                    <button className="back-button" onClick={handleBackToExplorerBoard}>
                      <i className="fas fa-arrow-left"></i>
                      <span>Back to Explorer Board</span>
                    </button>
                  </div>
                </div>

                <div className="details-content-full">
                  <div className="details-main-section">
                    <div className="details-section">
                      <h3>Description</h3>
                      <p>{selectedJourneyDetails.description}</p>
                    </div>

                    <div className="details-section">
                      <h3>Scope</h3>
                      <p><strong>Level:</strong> {selectedJourneyDetails.scope?.level || 'N/A'}</p>
                      {selectedJourneyDetails.scope?.countries && selectedJourneyDetails.scope.countries.length > 0 && (
                        <p><strong>Countries:</strong> {selectedJourneyDetails.scope.countries.join(', ')}</p>
                      )}
                      {selectedJourneyDetails.scope?.states && selectedJourneyDetails.scope.states.length > 0 && (
                        <p><strong>States:</strong> {selectedJourneyDetails.scope.states.join(', ')}</p>
                      )}
                    </div>

                    {selectedJourneyDetails.desires && selectedJourneyDetails.desires.length > 0 && (
                      <div className="details-section">
                        <h3>Desires</h3>
                        <div className="tag-list">
                          {selectedJourneyDetails.desires.map((desire, idx) => (
                            <span key={idx} className="tag">{desire}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedJourneyDetails.feelings && selectedJourneyDetails.feelings.length > 0 && (
                      <div className="details-section">
                        <h3>Feelings</h3>
                        <div className="tag-list">
                          {selectedJourneyDetails.feelings.map((feeling, idx) => (
                            <span key={idx} className="tag">{feeling}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedJourneyDetails.goals && selectedJourneyDetails.goals.length > 0 && (
                      <div className="details-section">
                        <h3>Goals</h3>
                        <div className="tag-list">
                          {selectedJourneyDetails.goals.map((goal, idx) => (
                            <span key={idx} className="tag">{goal}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedJourneyDetails.recommendedSeason && selectedJourneyDetails.recommendedSeason.length > 0 && (
                      <div className="details-section">
                        <h3>Recommended Season</h3>
                        <div className="tag-list">
                          {selectedJourneyDetails.recommendedSeason.map((season, idx) => (
                            <span key={idx} className="tag">{season}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedJourneyDetails.overallRating != null && selectedJourneyDetails.overallRating > 0 && (
                      <div className="details-section">
                        <div className="rating-header">
                          <h3>Rating</h3>
                        </div>
                        <p><i className="fas fa-star"></i> {selectedJourneyDetails.overallRating.toFixed(1)} Overall Rating</p>
                      </div>
                    )}

                    {/* Journey Locations List */}
                    {(() => {
                      const features = getGeoJSONFeatures();
                      
                      if (features.length === 0) {
                        return (
                          <div className="details-section">
                            <h3>Journey Locations</h3>
                            <p>No location data available for this journey.</p>
                          </div>
                        );
                      }
                      
                      // Get journeyProgress for this journey
                      const journeyProgress = selectedJourneyDetails.journeyProgress || [];
                      const visitedContinents = selectedJourneyDetails.visitedContinents || [];
                      const mapType = selectedJourneyDetails.mapType || '';
                      const isContinentStyling = mapType === 'continentStyling';
                      
                      // Helper function to check if a location is completed
                      const isLocationCompleted = (feature) => {
                        if (!journeyProgress || journeyProgress.length === 0) {
                          // For continentStyling, check if continent is visited even if no journeyProgress
                          if (isContinentStyling && visitedContinents.length > 0) {
                            const continent = feature.properties?.continent;
                            return continent && visitedContinents.includes(continent);
                          }
                          return false;
                        }
                        
                        // Check by property name
                        const locationName = feature.name || feature.properties?.name;
                        if (locationName && journeyProgress.includes(locationName)) {
                          return true;
                        }
                        
                        // Check by OBJECTID
                        const objectId = feature.properties?.OBJECTID;
                        if (objectId !== undefined && objectId !== null) {
                          // Handle both string and number comparisons
                          if (journeyProgress.some(progressId => 
                            String(progressId) === String(objectId) || Number(progressId) === Number(objectId)
                          )) {
                            return true;
                          }
                        }
                        
                        // Check by woe_id (for Google dataset matching, especially for continent journeys)
                        const woeId = feature.properties?.woe_id;
                        if (woeId !== undefined && woeId !== null) {
                          // Handle both string and number comparisons
                          if (journeyProgress.some(progressId => 
                            Number(progressId) === Number(woeId)
                          )) {
                            return true;
                          }
                        }
                        
                        // For continentStyling, check if continent is visited
                        if (isContinentStyling && visitedContinents.length > 0) {
                          const continent = feature.properties?.continent;
                          if (continent && visitedContinents.includes(continent)) {
                            return true;
                          }
                        }
                        
                        return false;
                      };
                      
                      // Sort features: completed locations first, then apply selected sort within each group
                      const sortedFeatures = [...features].sort((a, b) => {
                        // First, check completion status - completed locations come first
                        const aCompleted = isLocationCompleted(a);
                        const bCompleted = isLocationCompleted(b);
                        
                        if (aCompleted !== bCompleted) {
                          // If one is completed and the other isn't, completed comes first
                          return aCompleted ? -1 : 1;
                        }
                        
                        // Both are in the same completion group, now apply the selected sort
                        if (locationSortBy === 'alphabetical') {
                          // Sort alphabetically by location name
                          const nameA = (a.name || a.properties?.name || '').toLowerCase();
                          const nameB = (b.name || b.properties?.name || '').toLowerCase();
                          return nameA.localeCompare(nameB);
                        } else if (locationSortBy === 'distanceFromHome') {
                          // Sort by distance from home address
                          const centerA = getLocationCenter(a);
                          const centerB = getLocationCenter(b);
                          
                          if (!centerA || !centerB) return 0;
                          if (!userAddress || !userAddress.lat || !userAddress.lng) return 0;
                          
                          const distanceA = calculateDistance(
                            userAddress.lat,
                            userAddress.lng,
                            centerA.lat,
                            centerA.lng
                          );
                          const distanceB = calculateDistance(
                            userAddress.lat,
                            userAddress.lng,
                            centerB.lat,
                            centerB.lng
                          );
                          
                          return distanceA - distanceB;
                        } else if (locationSortBy === 'distanceFromNote') {
                          // Sort by distance from closest note
                          const centerA = getLocationCenter(a);
                          const centerB = getLocationCenter(b);
                          
                          if (!centerA || !centerB) return 0;
                          
                          // Get notes with coordinates
                          const notesWithCoords = notes.filter(note => {
                            const latLng = parseAndValidateLatLong(note.aiPromptResults?.lat_long?.value);
                            return latLng !== null;
                          });
                          
                          if (notesWithCoords.length === 0) return 0;
                          
                          // Find closest note distance for each location
                          const getClosestNoteDistance = (center) => {
                            let minDistance = Infinity;
                            notesWithCoords.forEach(note => {
                              const latLng = parseAndValidateLatLong(note.aiPromptResults?.lat_long?.value);
                              if (latLng) {
                                const distance = calculateDistance(center.lat, center.lng, latLng.lat, latLng.lng);
                                if (distance < minDistance) {
                                  minDistance = distance;
                                }
                              }
                            });
                            return minDistance;
                          };
                          
                          const distanceA = getClosestNoteDistance(centerA);
                          const distanceB = getClosestNoteDistance(centerB);
                          
                          return distanceA - distanceB;
                        }
                        
                        return 0;
                      });
                      
                      return (
                        <div className="details-section">
                          <div className="locations-header">
                            <h3>Journey Locations</h3>
                            <div className="locations-controls">
                              <label htmlFor="location-sort" className="sort-label">Sort by:</label>
                              <select
                                id="location-sort"
                                className="location-sort-select"
                                value={locationSortBy}
                                onChange={(e) => setLocationSortBy(e.target.value)}
                              >
                                <option value="alphabetical">Alphabetical</option>
                                <option value="distanceFromHome">Distance from Address</option>
                                <option value="distanceFromNote">Distance from Closest Note</option>
                              </select>
                            </div>
                          </div>
                          <p className="features-summary">
                            <strong>Total Locations:</strong> {selectedJourneyDetails.locationCount !== undefined ? selectedJourneyDetails.locationCount : features.length}
                          </p>
                          <ul className="locations-list">
                            {sortedFeatures.map((feature, idx) => {
                              // Handle both GeoJSON format and object format
                              // Prioritize name_en, then name, then fallback to OBJECTID or location number
                              const locationName = feature.properties?.name_en || 
                                                   feature.properties?.name || 
                                                   feature.name || 
                                                   feature.properties?.OBJECTID || 
                                                   `Location ${idx + 1}`;
                              const center = getLocationCenter(feature);
                              const isClickable = center !== null && onCenterMap !== undefined;
                              const isCompleted = isLocationCompleted(feature);
                              
                              // Get tooltip text with distance information
                              const tooltipText = getLocationDistanceTooltip(center, isClickable);
                              
                              // Get closest note for this location
                              const closestNote = center ? getClosestNote(center) : null;
                              
                              // Handler for creating a note for this location
                              const handleCreateNoteForLocation = async (e) => {
                                e.stopPropagation();
                                if (!center) return;
                                
                                try {
                                  // Fetch address from coordinates (similar to MainApp)
                                  const geocodifyApiKey = process.env.REACT_APP_GEOCODIFY_API_KEY;
                                  let address = 'Unknown address';
                                  
                                  if (geocodifyApiKey) {
                                    try {
                                      const response = await axios.get('https://api.geocodify.com/v2/reverse', {
                                        params: {
                                          api_key: geocodifyApiKey,
                                          lat: center.lat,
                                          lng: center.lng,
                                        },
                                      });
                                      
                                      if (response.data.response && response.data.response.features && response.data.response.features.length > 0) {
                                        address = response.data.response.features[0].properties.label || 'Unknown address';
                                        address = address.replace(/[^ -~]+/g, '').replace(/,/g, '');
                                      }
                                    } catch (error) {
                                      console.error('Error fetching address:', error);
                                    }
                                  }
                                  
                                  // Navigate to create-note with pre-populated data
                                  navigate('/create-note', {
                                    state: {
                                      clientId: null, // Will be set by CreateNote if needed
                                      manualTags: {
                                        Address: address,
                                      },
                                      lat_long: { value: `${center.lat},${center.lng}`, probability: 100.0 },
                                      emailSubject: `Visit ${locationName}`, // Pre-populate note name
                                    },
                                  });
                                } catch (error) {
                                  console.error('Error creating note for location:', error);
                                }
                              };
                              
                              // Get closest note name for display
                              const closestNoteName = closestNote 
                                ? (closestNote.manualPromptResults?.emailSubject || closestNote.emailSubject || 'Note')
                                : null;
                              
                              return (
                                <li 
                                  key={idx} 
                                  className={`location-name-item ${isClickable ? 'clickable' : ''} ${isCompleted ? 'completed' : ''} ${closestNote ? 'has-note' : ''} ${!isCompleted && center ? 'can-add-note' : ''}`}
                                  onClick={() => isClickable && handleLocationClick(feature)}
                                  title={tooltipText}
                                >
                                  <span className="location-name">
                                    {isCompleted && <i className="fas fa-check-circle completed-icon"></i>}
                                    {locationName}
                                  </span>
                                  {!isCompleted && center && (
                                    <div className="location-add-note-hover">
                                      {closestNote ? (
                                        <div className="location-note-info">
                                          <span className="note-info-text">
                                            Nearest note: <span className="note-name">{closestNoteName}</span> ({formatDistance(closestNote.distance)} away)
                                          </span>
                                          <span className="note-prompt">Create new note?</span>
                                        </div>
                                      ) : (
                                        <span className="note-prompt">Create new note?</span>
                                      )}
                                      <button
                                        className="add-note-button"
                                        onClick={handleCreateNoteForLocation}
                                        title={`Create note for ${locationName}`}
                                      >
                                        <i className="fas fa-plus"></i>
                                        Add Note
                                      </button>
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })()}

                  </div>

                  <div className="details-actions">
                    <button className="back-to-search-btn" onClick={handleBackToExplorerBoard} style={{ width: '100%', maxWidth: '300px' }}>
                      <i className="fas fa-arrow-left"></i>
                      Back to Explorer Board
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="details-error">
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: 'var(--text-tertiary)', marginBottom: 'var(--space-4)' }}></i>
                <p>Failed to load journey details.</p>
                <button className="back-button" onClick={handleBackToExplorerBoard}>
                  <i className="fas fa-arrow-left"></i>
                  <span>Back to Explorer Board</span>
                </button>
              </div>
            )}
          </div>
        ) : isLoading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading journeys...</span>
          </div>
        ) : journeys.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-map-marked-alt"></i>
            <h3>No Journeys Yet</h3>
            <p>Add your first journey to start exploring!</p>
            <button className="create-journey-btn modern-icon-button primary" onClick={() => setIsSearchModalOpen(true)}>
              <i className="fas fa-plus-circle"></i>
              <span>Add Journey</span>
            </button>
          </div>
        ) : (
          <JourneyList
            journeys={journeys}
            onJourneySelect={onJourneySelect}
            onJourneyUpdate={onJourneyUpdate}
            selectedJourneyId={selectedJourneyId}
            calculatingJourneyIds={calculatingJourneyIds}
          />
        )}
      </div>

      {/* Pagination - Hide when showing journey details */}
      {!selectedJourneyDetails && (
        <div className="pagination-section">
          <div className="pagination-info">
            <div className="pagination-stats" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <span className="stat-item">
                <span className="stat-number">{journeys.length}</span>
                <span className="stat-label">Showing</span>
              </span>
              <span className="stat-divider">•</span>
              <span className="stat-item">
                <span className="stat-number">{totalJourneys}</span>
                <span className="stat-label">Total</span>
              </span>
              <span className="stat-divider">•</span>
              <span className="stat-item">
                <span className="stat-number">{currentPage}</span>
                <span className="stat-label">Page {currentPage} of {totalPages}</span>
              </span>
              {onPageSizeChange && (
                <>
                  <span className="stat-divider">•</span>
                  <span className="stat-item">
                    <label className="stat-label" style={{ marginRight: '8px' }}>Per Page:</label>
                    <select
                      value={journeysPerPage}
                      onChange={(e) => onPageSizeChange(Number(e.target.value))}
                      disabled={isLoading}
                      style={{
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '4px',
                        background: 'var(--bg-elevated)',
                        color: 'var(--text-primary)',
                        cursor: isLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={0}>All</option>
                    </select>
                  </span>
                </>
              )}
            </div>
          </div>
          
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
              >
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, index) => {
                  const pageNumber = index + 1;
                  const isActive = pageNumber === currentPage;
                  const isNearActive = Math.abs(pageNumber - currentPage) <= 2;
                  
                  if (isActive || isNearActive || pageNumber === 1 || pageNumber === totalPages) {
                    return (
                      <button
                        key={pageNumber}
                        className={`page-btn ${isActive ? 'active' : ''}`}
                        onClick={() => onPageChange(pageNumber)}
                        disabled={isLoading}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                    return <span key={pageNumber} className="page-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
              >
                Next
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Journey Search Modal */}
      <JourneySearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onJourneySelect={onAddJourney}
        userPlan={userPlan}
      />

      {/* Create Journey Modal */}
      <CreateJourneyModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setReviewJourneyData(null);
          setCreateModalSource(null);
        }}
        onJourneyCreated={() => {
          // Refresh journeys list
          if (onCreateJourney) {
            onCreateJourney();
          }
          setReviewJourneyData(null); // Clear review data after creation
        }}
        initialJourney={reviewJourneyData}
        editPolygonOnly={createModalSource === 'polygonEdit'}
      />
    </div>
  );
};

export default ExplorerBoard;
